import { useEffect, useState } from 'react';
import { getSystemInfo, getActiveBrand } from '../services/api';

// One-time cache for /api/system/info (public, never changes mid-session).
let _publicCached = null;
let _publicInflight = null;
const _fetchPublic = () => {
  if (_publicCached !== null) return Promise.resolve(_publicCached);
  if (!_publicInflight) {
    _publicInflight = getSystemInfo()
      .then((res) => { _publicCached = res.data || {}; return _publicCached; })
      .catch(() => { _publicCached = {}; return _publicCached; });
  }
  return _publicInflight;
};

// Cache for the authenticated active-brand call, keyed by active_company_id
// so switching tenants invalidates the cached value automatically.
let _brandCached = null;
let _brandCachedKey = null;
let _brandInflight = null;
const _subscribers = new Set();
const _notify = (v) => { _subscribers.forEach((cb) => cb(v)); };

const _activeKey = () => localStorage.getItem('active_company_id') || '__own__';

const _fetchBrand = (force = false) => {
  const key = _activeKey();
  if (!force && _brandCached && _brandCachedKey === key) {
    return Promise.resolve(_brandCached);
  }
  if (_brandInflight && _brandCachedKey === key && !force) return _brandInflight;
  _brandCachedKey = key;
  _brandInflight = getActiveBrand()
    .then((res) => {
      _brandCached = res.data || {};
      _brandInflight = null;
      _notify(_brandCached);
      return _brandCached;
    })
    .catch(() => {
      // Not authenticated yet (e.g. login page) — just leave brand empty so
      // the public defaults kick in.
      _brandCached = null;
      _brandInflight = null;
      return null;
    });
  return _brandInflight;
};

/**
 * Returns the system info merged with the active company's brand. As the user
 * (or master_admin) switches tenants, calling `refreshBrand()` (or simply
 * reloading the page after switch) updates every consumer of this hook.
 */
export const useSystemInfo = () => {
  const [pub, setPub] = useState(_publicCached || {});
  const [brand, setBrand] = useState(_brandCached || null);

  useEffect(() => {
    let mounted = true;
    _fetchPublic().then((v) => { if (mounted) setPub(v || {}); });
    _fetchBrand().then((v) => { if (mounted) setBrand(v); });
    const cb = (v) => { if (mounted) setBrand(v); };
    _subscribers.add(cb);
    return () => { mounted = false; _subscribers.delete(cb); };
  }, []);

  // Brand from the active company wins over the env-level default; falls back
  // to the public defaults from /api/system/info if not authenticated yet.
  const merged = { ...pub, ...(brand || {}) };
  return {
    demo_mode: !!merged.demo_mode,
    brand_name: merged.brand_name || '360 Carexport',
    brand_short: merged.brand_short || merged.brand_name || '360 Carexport',
    brand_tagline: merged.brand_tagline || 'Streamline your car export operations from China to Algeria with our comprehensive management platform.',
    logo_url: merged.logo_url || null,
    primary_color: merged.primary_color || '#0c1a2e',
    company_id: merged.company_id || null,
    company_name: merged.company_name || null,
    // Use the demo-mode neutral SVG monogram whenever DEMO_MODE is on (the
    // tenant's own logo is intentionally hidden in demo deployments).
    use_generic_logo: !!merged.demo_mode,
    refreshBrand: () => _fetchBrand(true),
  };
};
